import { AnalyticsStore } from 'bernie-core';
import { FlightsFlexibleSearchResultCellTheme, FlightsFlexibleSearchHeaderCellTheme } from '__generated__/api';
import { trackAnalytics } from 'utility';
import { CheapestListingInfoStore } from '../stores';

const enum TextTheme {
  POSITIVE = 'positive',
  LINK = 'link'
}

export const isFlexibleSearchCellSelected = (theme: FlightsFlexibleSearchResultCellTheme): boolean => {
  const selectedCellThemes = [
    FlightsFlexibleSearchResultCellTheme.SEARCHED_DATE,
    FlightsFlexibleSearchResultCellTheme.SEARCHED_DATE_LOWEST_PRICE,
    FlightsFlexibleSearchResultCellTheme.SEARCHED_DATE_NO_PRICE,
    FlightsFlexibleSearchResultCellTheme.SEARCHED_DATE_UNAVAILABLE_FLIGHTS
  ];

  return selectedCellThemes.indexOf(theme) > -1;
};

export const getFlexibleSearchCellTextTheme = (theme: FlightsFlexibleSearchResultCellTheme) => {
  const themeMap = {
    LOWEST_PRICE: TextTheme.POSITIVE,
    NO_PRICE: TextTheme.LINK,
    SEARCHED_DATE_LOWEST_PRICE: TextTheme.POSITIVE,
    SEARCHED_DATE_NO_PRICE: TextTheme.LINK
  };

  return themeMap[theme];
};

const enum GridResultTheme {
  DEFAULT = 'default',
  NEGATIVE = 'negative',
  POSITIVE = 'positive',
  LINK = 'link',
  SELECTED_CELL = 'selected-cell',
  SELECTED_HEADER = 'selected-header',
  UNAVAILABLE = 'unavailable'
}

export const getFlexibleSearchGridResultTheme = (
  theme: FlightsFlexibleSearchResultCellTheme | FlightsFlexibleSearchHeaderCellTheme
) => {
  const themeMap = {
    DEFAULT: GridResultTheme.DEFAULT,
    HIGHEST_PRICE: GridResultTheme.NEGATIVE,
    LOWEST_PRICE: GridResultTheme.POSITIVE,
    NO_PRICE: GridResultTheme.LINK,
    SEARCHED_DATE: GridResultTheme.SELECTED_CELL,
    SEARCHED_DATE_HEADER: GridResultTheme.SELECTED_HEADER,
    SEARCHED_DATE_HIGHEST_PRICE: GridResultTheme.SELECTED_CELL,
    SEARCHED_DATE_LOWEST_PRICE: GridResultTheme.SELECTED_CELL,
    SEARCHED_DATE_NO_PRICE: GridResultTheme.SELECTED_CELL,
    SEARCHED_DATE_UNAVAILABLE_FLIGHTS: GridResultTheme.SELECTED_CELL,
    UNAVAILABLE_FLIGHTS: GridResultTheme.UNAVAILABLE
  };

  return themeMap[theme];
};

const formatAndParsePrice = (price: string) => {
  const regexForNumbersOnly = /[^0-9]/g;
  const formattedPrice = price.replace(regexForNumbersOnly, '');

  return parseInt(formattedPrice);
};

const formatToken = (rffrToken: string) => {
  return rffrToken && rffrToken.substr(rffrToken.lastIndexOf('.') + 1);
};

const getPriceDeviationPercent = (cheapestSearchedPriceString: string, flexPriceString: string) => {
  const flexPrice = formatAndParsePrice(flexPriceString);
  const priceDifference = formatAndParsePrice(cheapestSearchedPriceString) - flexPrice;
  const priceDeviationPercent = (priceDifference / flexPrice) * 100;

  return Math.round(priceDeviationPercent).toString().concat('%');
};

const fireAnalyticsForNffOrPriceDeviation = (
  cheapestListingInfoStore: CheapestListingInfoStore,
  searchedPrice: string,
  flexPrice: string,
  analyticsStore: AnalyticsStore
) => {
  const priceNotFound = '-';
  const isFlexSearchedDateNff = flexPrice && isNaN(formatAndParsePrice(flexPrice));
  const isSearchedDateNff = searchedPrice === priceNotFound;
  const isBothSearchAndFlexDateNFF = isFlexSearchedDateNff && isSearchedDateNff;
  if ((isSearchedDateNff || isFlexSearchedDateNff) && !isBothSearchAndFlexDateNFF) {
    trackAnalytics(cheapestListingInfoStore.nffAnalytics, analyticsStore);
  }
  if (!isFlexSearchedDateNff && !isSearchedDateNff) {
    const rfrrToken =
      cheapestListingInfoStore.deviationAnalytics && formatToken(cheapestListingInfoStore.deviationAnalytics.rfrr);
    const priceDeviationPercentage = getPriceDeviationPercent(searchedPrice, flexPrice);
    trackAnalytics(cheapestListingInfoStore.deviationAnalytics, analyticsStore, rfrrToken, priceDeviationPercentage);
  }
};

export const getValueText = (
  cheapestListingInfoStore: CheapestListingInfoStore,
  flexPrice: string,
  theme: FlightsFlexibleSearchResultCellTheme,
  analyticsStore: AnalyticsStore
) => {
  const cellSelected = isFlexibleSearchCellSelected(theme);
  const searchedPrice = cheapestListingInfoStore.price;

  if (searchedPrice && cellSelected && searchedPrice !== flexPrice) {
    fireAnalyticsForNffOrPriceDeviation(cheapestListingInfoStore, searchedPrice, flexPrice, analyticsStore);

    return searchedPrice;
  }

  return flexPrice;
};

export const getSelectedCellData = result => {
  const { accessibility, nextSearchCriteria } = result;

  return {
    accessibility: accessibility,
    nextSearchCriteria: nextSearchCriteria
  };
};

export const getFooterProps = (result, buttonText, clickCallback, analytics) => {
  const { journeyDate, valueText, summary } = result;

  return {
    analytics: analytics,
    buttonText: buttonText,
    dateText: journeyDate,
    onClick: clickCallback,
    priceText: valueText,
    subText: summary
  };
};

export const enum FlexSearchPerformance {
  FETCH_FLEX_SEARCH_START_MARK = 'Fetch Flexible Search Start',
  FETCH_FLEX_SEARCH_END_MARK = 'Fetch Flexible Search End',
  FETCH_FLEX_SEARCH_DURATION = 'Duration of Flexible Search Call',
  RENDER_FLEX_SEARCH_START_MARK = 'Flexible Search Start Render',
  RENDER_FLEX_SEARCH_END_MARK = 'Flexible Search End Render',
  RENDER_FLEX_SEARCH_DURATION = 'Duration of Flexible Search Render'
}
